import { z } from 'zod';

/**
 * Canal Grow uP na Twitch (GS16). O Programador do canal (`streaming.channel`)
 * planeia a grelha do canal da associacao; os streamers pedem horario pelo
 * `/painel`. Na grelha, os programas aceites nao se sobrepoem.
 */

const datetimeSchema = z.string().datetime({ offset: true });

/** O canal da associacao na Twitch. */
export const GROWUP_CHANNEL = 'growupesportstv';
/** Um programa dura no maximo 12 horas. */
export const CHANNEL_SLOT_MAX_HOURS = 12;

export const channelSlotStatusSchema = z.enum([
  'requested',
  'proposed',
  'accepted',
  'declined',
  'cancelled',
]);
export type ChannelSlotStatus = z.infer<typeof channelSlotStatusSchema>;

export const channelSlotSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  startsAt: datetimeSchema,
  endsAt: datetimeSchema,
  status: channelSlotStatusSchema,
  /** O streamer do programa; `null` = programa da casa. */
  streamer: z
    .object({ id: z.string().uuid(), channel: z.string(), displayName: z.string() })
    .nullable(),
  requestNote: z.string(),
  answerNote: z.string(),
  createdAt: datetimeSchema,
});
export type ChannelSlot = z.infer<typeof channelSlotSchema>;

/** `GET /api/streaming/channel?from=AAAA-MM-DD`: a semana na grelha e os pedidos por responder. */
export const channelWeekSchema = z.object({
  from: z.string(),
  to: z.string(),
  /** Aceites, propostos e pedidos que caem na semana. */
  slots: z.array(channelSlotSchema),
  /** Todos os pedidos a espera do programador, por ordem de chegada. */
  requests: z.array(channelSlotSchema),
});
export type ChannelWeek = z.infer<typeof channelWeekSchema>;

export const channelWeekQuerySchema = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

const timesSchema = z.object({
  startsAt: datetimeSchema,
  endsAt: datetimeSchema,
});

function checkTimes(v: { startsAt: string; endsAt: string }, ctx: z.RefinementCtx): void {
  const ms = new Date(v.endsAt).getTime() - new Date(v.startsAt).getTime();
  if (ms <= 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'o fim tem de ser depois do inicio',
      path: ['endsAt'],
    });
  } else if (ms > CHANNEL_SLOT_MAX_HOURS * 3600_000) {
    ctx.addIssue({
      code: 'custom',
      message: `no maximo ${String(CHANNEL_SLOT_MAX_HOURS)} horas`,
      path: ['endsAt'],
    });
  }
}

/** `POST`/`PUT /api/streaming/channel/slots[/:id]`: o programador marca um programa na grelha. */
export const channelSlotInputSchema = timesSchema
  .extend({
    title: z.string().trim().min(1, 'escreve o titulo').max(100),
    streamerId: z.string().uuid().nullable().default(null),
  })
  .superRefine(checkTimes);
export type ChannelSlotInput = z.input<typeof channelSlotInputSchema>;

/** `POST /api/me/channel/requests`: o streamer pede um horario no canal. */
export const channelRequestInputSchema = timesSchema
  .extend({
    title: z.string().trim().min(1, 'escreve o que vais fazer').max(100),
    note: z.string().trim().max(500).default(''),
  })
  .superRefine(checkTimes);
export type ChannelRequestInput = z.input<typeof channelRequestInputSchema>;

/**
 * `POST /api/streaming/channel/requests/:id/decision`: aceitar, recusar ou
 * propor outra hora (com `startsAt` e `endsAt`).
 */
export const channelDecisionSchema = z
  .object({
    decision: z.enum(['accept', 'decline', 'propose']),
    note: z.string().trim().max(500).default(''),
    startsAt: datetimeSchema.optional(),
    endsAt: datetimeSchema.optional(),
  })
  .refine((v) => v.decision !== 'propose' || (v.startsAt && v.endsAt), {
    message: 'escolhe a nova hora',
    path: ['startsAt'],
  })
  .superRefine((v, ctx) => {
    if (v.decision === 'propose' && v.startsAt && v.endsAt) {
      checkTimes({ startsAt: v.startsAt, endsAt: v.endsAt }, ctx);
    }
  })
  .refine((v) => v.decision !== 'decline' || v.note.length > 0, {
    message: 'diz ao streamer porque',
    path: ['note'],
  });
export type ChannelDecision = z.input<typeof channelDecisionSchema>;

/** `POST /api/me/channel/requests/:id/answer`: o streamer responde a uma proposta. */
export const channelProposalAnswerSchema = z.object({ accept: z.boolean() });

/** `GET /api/me/channel`: a grelha das proximas 2 semanas e os meus pedidos. */
export const myChannelSchema = z.object({
  channel: z.string(),
  upcoming: z.array(channelSlotSchema),
  mine: z.array(channelSlotSchema),
});
export type MyChannel = z.infer<typeof myChannelSchema>;
